import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';
import { useNavigate } from 'react-router-dom';

import { API_URL } from '../config/api';

const SalaryAllocation = () => {
  const navigate = useNavigate();
  const [salaryDetails, setSalaryDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 25,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    userId: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [salaryToDelete, setSalaryToDelete] = useState(null);
  
  // Check if user is admin or has admin-designated role from env
  const isAdminLike = () => {
    const role = localStorage.getItem('role') || '';
    const designationName = localStorage.getItem('designationName') || '';
    const normalizedDesignation = designationName.replace(/\u00A0/g, ' ').trim();
    const adminDesignation = process.env.REACT_APP_ADMIN_DESIGNATION || '';
    return role === 'admin' || (adminDesignation && normalizedDesignation === adminDesignation.trim());
  };

  useEffect(() => {
    if (isAdminLike()) {
      fetchEmployees();
    }
    fetchSalaryDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.limit, filters.year, filters.userId]);

  // Fetch employees list for admin filter
  const fetchEmployees = async () => {
    if (!API_URL) return;

    try {
      const payload = {
        action: 'get-active-employees-list'
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        setEmployees(result.employees || []);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // Fetch salary details
  const fetchSalaryDetails = async () => {
    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    setLoading(true);
    try {
      const currentUserId = localStorage.getItem('id');
      
      if (!currentUserId) {
        toast.error('User ID not found. Please login again.');
        return;
      }

      const payload = {
        action: 'list-salary-details',
        currentUserId: currentUserId,
        page: pagination.currentPage,
        limit: pagination.limit,
        year: filters.year,
        ...(filters.userId && { userId: filters.userId })
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        setSalaryDetails(result.data || []);
        setPagination(prev => ({
          ...prev,
          currentPage: result.currentPage || prev.currentPage,
          totalPages: result.totalPages || prev.totalPages,
          totalCount: result.totalCount || prev.totalCount,
          hasNextPage: (result.currentPage || prev.currentPage) < (result.totalPages || prev.totalPages),
          hasPreviousPage: (result.currentPage || prev.currentPage) > 1
        }));
      } else {
        toast.error(result?.message || 'Failed to fetch salary details');
      }
    } catch (error) {
      console.error('Error fetching salary details:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value, 10);
    setPagination(prev => ({ ...prev, limit: newLimit, currentPage: 1 }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const getSerialNumber = (index) => {
    return (pagination.currentPage - 1) * pagination.limit + index + 1;
  };

  const handleAdd = () => {
    navigate('/payroll-allocation-add');
  };

  const handleView = (salary) => {
    if (salary?.id) {
      // Navigate to view page or show details in modal
      // For now, we can navigate to edit page in view mode or create a view page
      navigate(`/payroll-allocation-view/${salary.id}`);
    } else {
      toast.error('Unable to view salary details. Missing identifier.');
    }
  };

  const handleEdit = (salary) => {
    if (salary?.id) {
      navigate(`/payroll-allocation-edit/${salary.id}`);
    } else {
      toast.error('Unable to edit salary details. Missing identifier.');
    }
  };

  const handleDelete = (salary) => {
    setSalaryToDelete(salary);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSalaryToDelete(null);
  };

  const confirmDelete = async () => {
    if (!salaryToDelete) return;

    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    const currentUserId = localStorage.getItem('id');
    
    if (!currentUserId) {
      toast.error('User ID not found. Please login again.');
      closeDeleteModal();
      return;
    }

    setLoading(true);
    try {
      const payload = {
        action: 'delete-salary-details',
        id: salaryToDelete.id,
        currentUserId: currentUserId
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result?.message || 'Salary details deleted successfully');
        closeDeleteModal();
        fetchSalaryDetails();
      } else {
        toast.error(result?.message || 'Failed to delete salary details');
      }
    } catch (error) {
      console.error('Error deleting salary details:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Generate year options (current year and previous 5 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let i = 0; i <= 5; i++) {
    yearOptions.push(currentYear - i);
  }

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title and Add Button */}
          <div className="row mb-3 employee-header-row">
            <div className="col-md-8">
              <h1 className="employee-title">
                Payroll Allocation
                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                View and manage salary allocation details for employees.
              </p>
            </div>
            {isAdminLike() && (
              <div className="col-md-4 employee-view-header-actions">
                <button type="button" className="employee-back-btn" onClick={handleAdd}>
                  Add Salary Allocation
                </button>
              </div>
            )}
          </div>

          {/* Salary Details Table Card */}
          <div className="card employee-content-card">
            <div className="card-body">
              <div className="employee-table-container">
                {/* Filters */}
                <div className="employee-search-container" style={{ display: 'flex', alignItems: 'flex-end', gap: '15px', flexWrap: 'wrap', marginBottom: '20px' }}>
                  <div className="form-group" style={{ minWidth: '150px' }}>
                    <label className="form-label" style={{ marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      Year
                    </label>
                    <select
                      className="form-control"
                      value={filters.year}
                      onChange={(e) => handleFilterChange('year', parseInt(e.target.value))}
                    >
                      {yearOptions.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  
                  {isAdminLike() && (
                    <div className="form-group" style={{ minWidth: '200px' }}>
                      <label className="form-label" style={{ marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                        Employee
                      </label>
                      <select
                        className="form-control"
                        value={filters.userId}
                        onChange={(e) => handleFilterChange('userId', e.target.value)}
                      >
                        <option value="">All Employees</option>
                        {employees.map(emp => (
                          <option key={emp.userId} value={emp.userId}>
                            {emp.fullName || '-'}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {loading ? (
                  <Loading size={120} />
                ) : (
                  <>
                    <div className="employee-table-wrapper">
                      <table className="employee-table">
                        <thead>
                          <tr>
                            <th>SI No.</th>
                            <th>Name</th>
                            <th>Year</th>
                            <th>Created At</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {salaryDetails.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="employee-empty" style={{ textAlign: 'center', padding: '40px' }}>
                                <i className="fas fa-inbox" style={{ fontSize: '48px', color: '#ccc', marginBottom: '10px' }}></i>
                                <p style={{ color: '#999', margin: '10px 0 0 0' }}>No salary details found</p>
                              </td>
                            </tr>
                          ) : (
                            salaryDetails.map((salary, index) => (
                              <tr key={salary.id}>
                                <td>{getSerialNumber(index)}</td>
                                <td>{salary.name || '-'}</td>
                                <td>{salary.year || '-'}</td>
                                <td>{formatDate(salary.createdAt)}</td>
                                <td>
                                  <div className="employee-actions">
                                    <button
                                      className="employee-action-btn employee-action-view"
                                      onClick={() => handleView(salary)}
                                      title="View"
                                    >
                                      <i className="fa-solid fa-eye"></i>
                                    </button>
                                    <button
                                      className="employee-action-btn employee-action-edit"
                                      onClick={() => handleEdit(salary)}
                                      title="Edit"
                                    >
                                      <i className="fa-solid fa-pencil"></i>
                                    </button>
                                    <button
                                      className="employee-action-btn employee-action-delete"
                                      onClick={() => handleDelete(salary)}
                                      title="Delete"
                                    >
                                      <i className="fa-solid fa-trash"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {salaryDetails.length > 0 && pagination.totalPages > 0 && (
                      <div className="employee-pagination">
                        <div className="pagination-limit-container" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: 'auto' }}>
                          <label htmlFor="salaryPaginationLimit" style={{ margin: 0, fontSize: '14px', color: '#495057' }}>
                            Show:
                          </label>
                          <select
                            id="salaryPaginationLimit"
                            value={pagination.limit}
                            onChange={handleLimitChange}
                            style={{
                              padding: '6px 10px',
                              fontSize: '14px',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              minWidth: '70px'
                            }}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>
                        </div>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(1)}
                            disabled={pagination.currentPage === 1}
                          >
                            «
                          </button>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={pagination.currentPage === 1}
                          >
                            ‹
                          </button>
                          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              className={`pagination-btn ${pagination.currentPage === page ? 'active' : ''}`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          ))}
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={pagination.currentPage === pagination.totalPages}
                          >
                            ›
                          </button>
                          <button
                            className="pagination-btn"
                            onClick={() => handlePageChange(pagination.totalPages)}
                            disabled={pagination.currentPage === pagination.totalPages}
                          >
                            »
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h5 className="modal-title">Delete Salary Details</h5>
              <button
                type="button"
                className="modal-close"
                onClick={closeDeleteModal}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete salary details for <strong>{salaryToDelete?.name || 'this employee'}</strong> for year <strong>{salaryToDelete?.year || ''}</strong>?</p>
              <p style={{ color: '#dc3545', marginTop: '10px', fontSize: '14px' }}>
                <i className="fas fa-exclamation-triangle"></i> This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeDeleteModal}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDelete}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Deleting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash"></i> Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryAllocation;
